/**
 * LoanTypesAdmin.tsx — Admin > Payments > Loan Types.
 *
 * Legacy's Loan Types (admin/admin_loan_types_list.php): the account's loan
 * templates, which fill in a new loan's terms on the Loans page. In the Lead
 * Sources layout (see components/admin/AdminList.tsx):
 *   - Title, Loan Term, Due Day, Late Fee and Interest, sorted by title;
 *     search by title; Show deleted;
 *   - "Add loan type" top right; a click on a live row opens it; Edit / Delete
 *     on a live row, Restore on a deleted one;
 *   - legacy's form: Title *, Payment Interval (monthly), Due Day (1–28),
 *     Loan Term (months) *, Late Fee Amount, Interest Rate (%) and Interest
 *     Type (simple).
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live type still opens, read-only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { getErrorMessage } from '@/services/apiClient';
import {
  DUE_DAY_OPTIONS,
  INTEREST_TYPE_OPTIONS,
  LATE_FEE_MAX,
  LOAN_INTERVAL_OPTIONS,
  LOAN_MONTHS_MAX,
  LOAN_TYPE_TITLE_MAX,
  createLoanType,
  deleteLoanType,
  listLoanTypes,
  restoreLoanType,
  updateLoanType,
  type AdminLoanType,
  type LoanTypePayload,
} from '@/services/loanTypesAdmin.service';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [
  { key: 'title', header: 'Title', sortable: true },
  { key: 'term', header: 'Loan Term', sortable: true, className: 'w-32' },
  { key: 'dueDay', header: 'Due Day', sortable: true, className: 'w-28' },
  { key: 'lateFee', header: 'Late Fee', sortable: true, className: 'w-28' },
  { key: 'interest', header: 'Interest', sortable: true, className: 'w-36' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

function loanTypeTitle(t: AdminLoanType): string {
  return t.title.trim() || `Loan type #${t.id}`;
}

function money(n: number): string {
  return `$${n.toFixed(2)}`;
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const SORT_COMPARE: ListSortCompare<AdminLoanType> = {
  title: (a, b) => compareText(loanTypeTitle(a), loanTypeTitle(b)),
  term: (a, b) => a.loanMonths - b.loanMonths,
  dueDay: (a, b) => a.dueDay - b.dueDay,
  lateFee: (a, b) => a.lateFee - b.lateFee,
  interest: (a, b) => a.interestRate - b.interestRate,
};

export function LoanTypesAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [types, setTypes] = useState<AdminLoanType[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AdminLoanType | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminLoanType | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setTypes(await listLoanTypes());
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load loan types'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () => types.filter((t) => (showDeleted || t.active) && (query === '' || t.title.toLowerCase().includes(query))),
    [types, showDeleted, query],
  );
  const sorting = useListSort(rows, { initialKey: 'title', compare: SORT_COMPARE });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  async function handleDelete() {
    if (!deleting) return;
    const { id } = deleting;
    try {
      await deleteLoanType(id);
      setTypes((prev) => prev.map((t) => (t.id === id ? { ...t, active: false } : t)));
      toast.success('Loan type deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The loan type could not be deleted.'));
    }
  }

  async function handleRestore(t: AdminLoanType) {
    try {
      await restoreLoanType(t.id);
      setTypes((prev) => prev.map((x) => (x.id === t.id ? { ...x, active: true } : x)));
      toast.success('Loan type restored');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The loan type could not be restored.'));
    }
  }

  return (
    <AdminListPage
      title="Loan Types"
      subtitle="Loan templates. Choosing one on a new loan fills in its terms."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add loan type
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search loan types..."
          searchLabel="Search loan types"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="loan-types-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading loan types…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No loan types found' : 'No loan types yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a loan type to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Loan types" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((t) => {
              const deleted = !t.active;
              const title = loanTypeTitle(t);
              return (
                <tr
                  key={t.id}
                  data-loan-type-id={t.id}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  onClick={deleted ? undefined : () => setEditing(t)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="loan-type-title">
                      {title}
                    </AdminRowName>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    {t.loanMonths} {t.loanMonths === 1 ? 'month' : 'months'}
                  </td>
                  <td className={ADMIN_TD_CLASS}>{t.dueDay > 0 ? t.dueDay : '—'}</td>
                  <td className={ADMIN_TD_CLASS}>{money(t.lateFee)}</td>
                  <td className={ADMIN_TD_CLASS}>
                    {t.interestRate}% {capitalise(t.interestType)}
                  </td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${title}`} onClick={() => void handleRestore(t)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${title}`} onClick={() => setEditing(t)} />
                            <AdminRowAction kind="delete" label={`Delete ${title}`} onClick={() => setDeleting(t)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['loan type', 'loan types']} />
        </>
      )}

      {editing && (
        <LoanTypeFormModal
          key={editing === 'new' ? 'new' : editing.id}
          loanType={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${loanTypeTitle(deleting)}"?` : ''}
        message="New loans can no longer use it; loans made from it keep their terms. You can restore it from Show deleted."
        confirmLabel="Delete loan type"
        variant="danger"
      />
    </AdminListPage>
  );
}

interface LoanTypeForm {
  title: string;
  loanInterval: string;
  dueDay: number;
  loanMonths: string;
  lateFee: string;
  interestRate: string;
  interestType: string;
}

/** The form's problems, keyed by field (an empty object = it can be saved). */
function loanTypeFormErrors(f: LoanTypeForm): Partial<Record<keyof LoanTypeForm, string>> {
  const errors: Partial<Record<keyof LoanTypeForm, string>> = {};
  if (f.title.trim() === '') errors.title = 'Title is required';
  const months = Number(f.loanMonths);
  if (f.loanMonths.trim() === '') errors.loanMonths = 'Loan term is required';
  else if (!Number.isInteger(months) || months < 1 || months > LOAN_MONTHS_MAX) errors.loanMonths = `Loan term must be a whole number of months, 1 to ${LOAN_MONTHS_MAX}`;
  const fee = Number(f.lateFee);
  if (f.lateFee.trim() === '' || !Number.isFinite(fee) || fee < 0 || fee > LATE_FEE_MAX) errors.lateFee = 'Late fee must be an amount of 0 or more';
  const rate = Number(f.interestRate);
  if (f.interestRate.trim() === '' || !Number.isFinite(rate) || rate < 0 || rate > 100) errors.interestRate = 'Interest rate must be 0 to 100';
  return errors;
}

function LoanTypeFormModal({
  loanType,
  onClose,
  onSaved,
}: {
  loanType: AdminLoanType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = loanType === null;
  const t = useTrackedForm<LoanTypeForm>(
    () => ({
      title: loanType?.title ?? '',
      loanInterval: loanType?.loanInterval ?? 'monthly',
      // Legacy's form showed 1 when a type had none.
      dueDay: loanType && loanType.dueDay >= 1 && loanType.dueDay <= 28 ? loanType.dueDay : 1,
      loanMonths: loanType ? String(loanType.loanMonths) : '',
      lateFee: loanType ? String(loanType.lateFee) : '0',
      interestRate: loanType ? String(loanType.interestRate) : '0',
      interestType: loanType?.interestType ?? 'simple',
    }),
    { tracking: !isNew },
  );
  const [saving, setSaving] = useState(false);
  const fieldErrors = loanTypeFormErrors(t.form);
  const errors = Object.values(fieldErrors);
  const state = (key: keyof LoanTypeForm) => (fieldErrors[key] ? 'error' : 'none') as 'error' | 'none';

  async function save() {
    if (errors.length > 0) return;
    setSaving(true);
    try {
      const data: LoanTypePayload = {
        title: t.form.title,
        loanInterval: t.form.loanInterval,
        dueDay: t.form.dueDay,
        loanMonths: Number(t.form.loanMonths),
        lateFee: Number(t.form.lateFee),
        interestRate: Number(t.form.interestRate),
        interestType: t.form.interestType,
      };
      if (loanType) await updateLoanType(loanType.id, data);
      else await createLoanType(data);
      toast.success(isNew ? 'Loan type created' : 'Loan type updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The loan type could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Loan Type' : 'Edit Loan Type'}
      isNew={isNew}
      dirty={isNew ? t.form.title.trim() !== '' || t.form.loanMonths.trim() !== '' : t.dirty}
      saving={saving}
      errors={errors}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <DialogField
          label="Title"
          htmlFor="loan-type-title"
          required
          state={state('title')}
          labelHint={fieldErrors.title ? '— required' : undefined}
          badge={t.badge('title', 'Title')}
          className="sm:col-span-2"
        >
          <Input
            id="loan-type-title"
            value={t.form.title}
            placeholder="e.g. 12-month plan"
            maxLength={LOAN_TYPE_TITLE_MAX}
            onChange={(e) => t.patch({ title: e.target.value })}
            className={fieldClasses.input(state('title'))}
          />
        </DialogField>
        <DialogField label="Payment Interval" htmlFor="loan-type-interval" badge={t.badge('loanInterval', 'Payment Interval')}>
          <Select<string>
            id="loan-type-interval"
            options={LOAN_INTERVAL_OPTIONS}
            value={t.form.loanInterval}
            onChange={(v) => t.patch({ loanInterval: v ?? 'monthly' })}
          />
        </DialogField>
        <DialogField label="Due Day" htmlFor="loan-type-due-day" badge={t.badge('dueDay', 'Due Day')} description="Day of the month a payment is due.">
          <Select<number>
            id="loan-type-due-day"
            options={DUE_DAY_OPTIONS}
            value={t.form.dueDay}
            onChange={(v) => t.patch({ dueDay: v ?? 1 })}
          />
        </DialogField>
        <DialogField
          label="Loan Term (months)"
          htmlFor="loan-type-months"
          required
          state={state('loanMonths')}
          labelHint={fieldErrors.loanMonths && t.form.loanMonths.trim() === '' ? '— required' : undefined}
          badge={t.badge('loanMonths', 'Loan Term (months)')}
        >
          <Input
            id="loan-type-months"
            type="number"
            min={1}
            max={LOAN_MONTHS_MAX}
            step={1}
            value={t.form.loanMonths}
            placeholder="e.g. 12"
            onChange={(e) => t.patch({ loanMonths: e.target.value })}
            className={fieldClasses.input(state('loanMonths'))}
          />
        </DialogField>
        <DialogField label="Late Fee Amount" htmlFor="loan-type-late-fee" state={state('lateFee')} badge={t.badge('lateFee', 'Late Fee Amount')}>
          <Input
            id="loan-type-late-fee"
            type="number"
            min={0}
            step="0.01"
            leftAddon="$"
            value={t.form.lateFee}
            onChange={(e) => t.patch({ lateFee: e.target.value })}
            className={fieldClasses.input(state('lateFee'))}
          />
        </DialogField>
        <DialogField label="Interest Rate" htmlFor="loan-type-interest-rate" state={state('interestRate')} badge={t.badge('interestRate', 'Interest Rate')}>
          <Input
            id="loan-type-interest-rate"
            type="number"
            min={0}
            max={100}
            step="0.01"
            rightAddon="%"
            value={t.form.interestRate}
            onChange={(e) => t.patch({ interestRate: e.target.value })}
            className={fieldClasses.input(state('interestRate'))}
          />
        </DialogField>
        <DialogField label="Interest Type" htmlFor="loan-type-interest-type" badge={t.badge('interestType', 'Interest Type')}>
          <Select<string>
            id="loan-type-interest-type"
            options={INTEREST_TYPE_OPTIONS}
            value={t.form.interestType}
            onChange={(v) => t.patch({ interestType: v ?? 'simple' })}
          />
        </DialogField>
      </div>
    </AdminFormModal>
  );
}
